import API from "./axios";

/**
 * To-do tasks — every login can give work to a colleague (or himself).
 *
 * WHO SEES / DOES WHAT is decided on the server: a task's detail carries
 * `allowedActions` (COMMENT, ACCEPT, DECLINE, CHANGE_DATE, DONE, BLOCK,
 * UNBLOCK, VERIFY, SEND_BACK, CANCEL, NOT_DONE) and `canEdit` for THIS
 * viewer, and the action endpoint refuses anything else with the reason —
 * the screens never re-derive the rules.
 *
 * A task given to someone arrives NOT ACCEPTED (status OPEN): he accepts it
 * with the date he will complete it by (`promisedDate`), or declines it with
 * a reason. The detail lists who has opened it (`seenBy`).
 *
 * Files (any kind, 10 MB each; photos are shrunk) are uploaded first —
 * uploadImage(file) → { id, url, contentType, image } — and their ids sent
 * with the task or the action (`attachmentIds`).
 */
export const todoTaskApi = {
  /**
   * view: MINE | WAITING_ON_ME | TO_CHECK | ASSIGNED_BY_ME | CLOSED | ALL (owner/admin).
   * assignedTo: one person's tasks only — all of them for the owner / admin,
   * only the ones you gave for anyone else.
   */
  list: (view, q, config, assignedTo) => API.get("/todo-tasks", {
    params: { view, q: q || undefined, assignedTo: assignedTo || undefined },
    ...(config || {}),
  }),
  /** Counts + the first tasks to look at. Silent: the top bar polls it. */
  summary: () => API.get("/todo-tasks/summary", { silent: true }),
  /** Active colleagues, you first — each with `pendingCount` for the "Assigned to" filter. */
  assignableUsers: () => API.get("/todo-tasks/assignable-users"),
  /** The task with its history; opening it marks it read. */
  get: (id, config) => API.get(`/todo-tasks/${id}`, config),
  /**
   * { title, description, priority, dueDate, attachmentIds, and either
   *   assignedToIds: [..] (a copy each — none = yourself)
   *   or outside: true + outsidePartyId (outside people list) + outsideParty: its label }
   * Returns the first copy.
   */
  create: (body) => API.post("/todo-tasks", body),
  edit: (id, body) => API.put(`/todo-tasks/${id}`, body),
  /** { action, body, promisedDate (ACCEPT / CHANGE_DATE, yyyy-mm-dd), blockedById, attachmentIds } */
  act: (id, body) => API.post(`/todo-tasks/${id}/actions`, body),
  /** Any file (the name is kept for its type); photos are shrunk on the server. */
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return API.post("/todo-tasks/attachments", fd, { headers: { "Content-Type": undefined } });
  },
  discardImage: (attachmentId) => API.delete(`/todo-tasks/attachments/${attachmentId}`),
  /**
   * The list as a PDF — the same view / search / person as the screen, so it
   * holds exactly what the screen shows. Blob response.
   */
  listPdf: (view, q, assignedTo) => API.get("/todo-tasks/pdf", {
    params: { view, q: q || undefined, assignedTo: assignedTo || undefined },
    responseType: "blob",
  }),
  /** One task as a PDF: details, dates, seen, history with photos, files. Not marked read. */
  taskPdf: (id) => API.get(`/todo-tasks/${id}/pdf`, { responseType: "blob" }),
  /** An attached file's bytes (the `url` the task gives, "/api/files?path=…") — to share it with the PDF. */
  fileBlob: (url) => API.get(/^https?:\/\//i.test(url) ? url : url.replace(/^\/api(?=\/)/, ""), { responseType: "blob" }),
};

export default todoTaskApi;
